import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Printer, RefreshCw } from "lucide-react";
import { api, errMsg, setAuth, getProfile, exportUrl, weekPdfUrl } from "./api";
import { Shell, Panel, BigBtn, GhostBtn, Field, inputCls, Spinner } from "./ui";

const TABS = ["Dashboard", "Week", "Requests", "Reports", "Tasks", "Cleans", "Videos", "Settings", "Outbox", "Audit"];

function Week() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/manager/week-overview").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!data) return <Spinner />;
  const tag = (t) => t.chlorine ? " (once per venue day)" : t.per_class ? " (repeats each session)" : t.conditional ? " (if needed)" : "";
  return (
    <div data-testid="manager-week" data-print="week-overview">
      <style>{`@media print {
        body * { visibility: hidden; }
        [data-print="week-overview"], [data-print="week-overview"] * { visibility: visible; }
        [data-print="week-overview"] { position: absolute; left: 0; top: 0; width: 100%; }
        [data-print="week-overview"] .print-hide { display: none; }
      }`}</style>
      <div className="flex items-center justify-between gap-3 mb-4 print-hide">
        <p className="text-sm text-white/55">Live overview — always reflects the current task templates, big cleans and shift settings.</p>
        <a data-testid="week-pdf-button" href={weekPdfUrl()}
          className="shrink-0 inline-flex items-center min-h-[44px] rounded-xl bg-[#ff2e4d] text-black font-bold uppercase tracking-wide text-xs px-4 active:scale-[0.98] transition">
          <Download size={15} className="inline mr-1.5" />A4 PDF
        </a>
      </div>

      <h3 className="text-xl uppercase italic mb-2" style={{ fontFamily: "Anton, sans-serif" }}>What each shift involves</h3>
      <div className="space-y-4 mb-8">
        {data.shift_defs.map((s) => (
          <Panel key={s.key} className="p-4" data-testid={`week-shift-${s.key}`}>
            <p className="font-semibold uppercase" style={{ fontFamily: "Anton, sans-serif" }}>
              {s.label}
              <span className="text-xs text-white/45 normal-case ml-2" style={{ fontFamily: "Archivo" }}>
                {s.days.length === 5 ? "Mon–Fri" : "Sat & Sun"}
                {s.configured && s.start ? ` · Between ${s.start} – ${s.end}` : ""}
                {s.closure === "final" ? " · responsible for final closure" : " · hands over to next shift"}
              </span>
            </p>
            {data.sections.map((sec) => (
              s.sections[sec].length > 0 && (
                <div key={sec} className="mt-3">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#ff2e4d]">{data.section_labels[sec]}</p>
                  {sec === "anytime" && (
                    <p className="text-xs text-white/45 mt-0.5">• This shift's big clean (see week grid below) — complete once during the shift</p>
                  )}
                  <ul className="mt-1 space-y-0.5">
                    {s.sections[sec].map((t, i) => (
                      <li key={i} className="text-sm text-white/75">• {t.group ? <span className="text-white/45">{t.group}: </span> : ""}{t.title}<span className="text-white/35">{tag(t)}</span></li>
                    ))}
                  </ul>
                </div>
              )
            ))}
          </Panel>
        ))}
      </div>

      <h3 className="text-xl uppercase italic mb-2" style={{ fontFamily: "Anton, sans-serif" }}>Week at a glance — big cleans</h3>
      <div className="space-y-3">
        {data.week.map((d) => (
          <Panel key={d.weekday} className="p-4" data-testid={`week-day-${d.weekday}`}>
            <p className="font-semibold uppercase" style={{ fontFamily: "Anton, sans-serif" }}>{d.label}</p>
            {d.shifts.map((sh) => (
              <div key={sh.key} className="mt-2">
                <p className="text-sm text-white/60">• <b className="text-white/85">{sh.label}:</b>{" "}
                  {sh.big_cleans.length === 0 && <span className="text-white/40">standard checklist — no larger cleaning assignment</span>}
                </p>
                {sh.big_cleans.map((bc) => (
                  <div key={bc.title} className="ml-4 mt-1">
                    <p className="text-sm text-[#ff2e4d] font-semibold">Big clean — {bc.title}</p>
                    <ul className="space-y-0.5">
                      {bc.steps.map((st, i) => (
                        <li key={i} className="text-sm text-white/70">&nbsp;&nbsp;– {st.group ? <span className="text-white/45">{st.group}: </span> : ""}{st.title}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </Panel>
        ))}
      </div>
    </div>
  );
}

function ManagerLogin({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/manager", { email, password });
      setAuth(data.token, { name: data.name, role: "manager", kind: "manager" });
      onDone();
    } catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  };

  return (
    <Shell title="Manager" back="/staff">
      <form onSubmit={submit} className="space-y-4 max-w-sm mx-auto mt-8" data-testid="manager-login-form">
        <h2 className="text-2xl uppercase italic" style={{ fontFamily: "Anton, sans-serif" }}>Manager sign in</h2>
        <p className="text-white/50 text-sm">Separate credential — the staff role picker never grants manager access.</p>
        <input data-testid="manager-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} autoComplete="username" />
        <input data-testid="manager-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} autoComplete="current-password" />
        {err && <p data-testid="manager-login-error" className="text-[#ff2e4d] text-sm">{err}</p>}
        <BigBtn data-testid="manager-login-button" type="submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</BigBtn>
      </form>
    </Shell>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/manager/dashboard").then((r) => setStats(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!stats) return <Spinner />;
  const cards = [
    ["Shifts signed off today", stats.submitted_today], ["Shifts in progress", stats.active_today],
    ["Open restock requests", stats.open_restock], ["Urgent restocks", stats.urgent_restock],
    ["Bikes unavailable", stats.bikes_out], ["Open bike faults", stats.open_faults],
    ["Task exceptions", stats.exceptions], ["Emails pending/failed", stats.email_pending],
  ];
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="manager-dashboard">
      {cards.map(([label, val]) => (
        <Panel key={label} className="p-4">
          <p className="text-3xl" style={{ fontFamily: "Anton, sans-serif" }}>{val}</p>
          <p className="text-xs text-white/50 mt-1">{label}</p>
        </Panel>
      ))}
      <div className="col-span-2 flex gap-2 flex-wrap mt-2">
        {["submissions", "restock", "bikes"].map((t) => (
          <a key={t} href={exportUrl(t)} data-testid={`export-${t}`}
            className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-xl border border-white/20 text-sm text-white/80">
            <Download size={15} /> Export {t} CSV
          </a>
        ))}
      </div>
    </div>
  );
}

function Requests() {
  const [reqs, setReqs] = useState(null);
  const load = () => api.get("/restock?all=true").then((r) => setReqs(r.data.requests)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const setStatus = async (r, status) => {
    try { await api.post(`/restock/${r.request_id}/update`, { status }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!reqs) return <Spinner />;
  return (
    <div className="space-y-3" data-testid="manager-requests">
      {reqs.length === 0 && <p className="text-white/40 text-sm">No requests yet.</p>}
      {reqs.map((r) => (
        <Panel key={r.request_id} className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-semibold">{r.item} {r.urgency === "urgent" && <span className="text-[#ff2e4d] text-xs uppercase">Urgent</span>}</p>
              <p className="text-xs text-white/45">{r.request_id} · {r.created_by} · {r.status}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap mt-3">
            {["acknowledged", "ordered", "received", "closed", "cancelled"].map((s) => (
              <button key={s} data-testid={`request-status-${r.request_id}-${s}`} onClick={() => setStatus(r, s)}
                className={`min-h-[40px] px-3 rounded-full border text-xs capitalize ${r.status === s ? "border-[#ff2e4d] text-[#ff2e4d]" : "border-white/15 text-white/50"}`}>{s}</button>
            ))}
          </div>
        </Panel>
      ))}
    </div>
  );
}

function Reports() {
  const [subs, setSubs] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => { api.get("/manager/submissions").then((r) => setSubs(r.data.submissions)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!subs) return <Spinner />;
  if (detail) {
    const snap = detail.snapshot || [];
    return (
      <div data-testid="report-detail">
        <button onClick={() => setDetail(null)} className="text-sm text-white/50 underline underline-offset-4 mb-3 min-h-[44px]">← All reports</button>
        <Panel className="p-5 space-y-3">
          <p className="text-xl uppercase" style={{ fontFamily: "Anton, sans-serif" }}>{detail.report_ref}</p>
          <p className="text-sm text-white/55">{detail.business_date} · {detail.shift_type} · signed by {detail.signed_by?.name} ({detail.signed_by?.role})</p>
          {detail.handover && <p className="text-sm"><b>Handover:</b> {detail.handover}</p>}
          {detail.signature && <img src={detail.signature} alt="signature" className="bg-black/50 border border-white/15 rounded-xl max-h-28" data-testid="report-signature" />}
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {snap.map((t) => (
              <p key={t.task_id} className="text-sm">
                <span className={t.status === "done" ? "text-emerald-400" : "text-amber-400"}>[{t.status}]</span> {t.title}
                {t.reason ? <span className="text-white/45"> — {t.reason}</span> : ""}
                {t.done_by ? <span className="text-white/35"> · {t.done_by}</span> : ""}
              </p>
            ))}
          </div>
          {detail.amendments?.length > 0 && (
            <div className="border-t border-white/10 pt-3">
              <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Amendments</p>
              {detail.amendments.map((a, i) => (
                <p key={i} className="text-xs text-white/55">{a.by}: {a.before} → {a.after} — {a.reason}</p>
              ))}
            </div>
          )}
        </Panel>
      </div>
    );
  }
  return (
    <div className="space-y-2.5" data-testid="manager-reports">
      {subs.length === 0 && <p className="text-white/40 text-sm">No signed shift reports yet.</p>}
      {subs.map((s) => (
        <button key={s.shift_id} data-testid={`report-item-${s.shift_id}`} className="block w-full text-left"
          onClick={() => api.get(`/manager/submissions/${s.shift_id}`).then((r) => setDetail(r.data))}>
          <Panel className="p-4 active:scale-[0.99] transition">
            <p className="font-semibold">{s.report_ref} — {s.business_date} {s.shift_type}</p>
            <p className="text-xs text-white/45">Signed by {s.signed_by?.name} · email: {s.email_status || "—"}</p>
          </Panel>
        </button>
      ))}
    </div>
  );
}

function Tasks() {
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");
  const [group, setGroup] = useState("");
  const [section, setSection] = useState("anytime");
  const load = () => api.get("/manager/templates").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!data) return <Spinner />;
  const toggle = async (t) => {
    try { await api.patch(`/manager/templates/${t.template_id}`, { active: !t.active }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const add = async () => {
    try {
      await api.post("/manager/templates", { title, section, group: group.trim() || null, role: "admin" });
      toast.success("Task template added"); setTitle(""); setGroup(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="manager-tasks">
      <Panel className="p-4 space-y-3 mb-5">
        <p className="text-xs uppercase tracking-widest text-white/40">New Admin task template</p>
        <input data-testid="template-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task title" className={inputCls} />
        <select data-testid="template-section-select" value={section} onChange={(e) => setSection(e.target.value)} className={inputCls}>
          {Object.entries(data.section_labels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input data-testid="template-group-input" value={group} onChange={(e) => setGroup(e.target.value)} placeholder="Group within Anytime (e.g. Laundry) — optional" className={inputCls} />
        <GhostBtn data-testid="template-add-button" onClick={add} disabled={!title.trim()} className="w-full">Add template</GhostBtn>
      </Panel>
      <div className="space-y-1.5 max-h-[55vh] overflow-y-auto">
        {data.templates.map((t) => (
          <div key={t.template_id} className={`flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#141414] px-3 py-2 ${!t.active ? "opacity-40" : ""}`}>
            <p className="text-sm min-w-0 truncate">{t.title} <span className="text-white/35 text-xs">· {t.role} · {data.section_labels[t.section] || t.section}{t.group ? ` / ${t.group}` : ""} · v{t.version}</span></p>
            <button data-testid={`template-toggle-${t.template_id}`} onClick={() => toggle(t)}
              className="min-h-[40px] px-3 rounded-full border border-white/20 text-xs shrink-0">{t.active ? "Disable" : "Enable"}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Cleans() {
  const [data, setData] = useState(null);
  const load = () => api.get("/manager/big-cleans").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!data) return <Spinner />;
  const slots = [];
  for (let d = 0; d < 5; d++) { slots.push([d, "morning"]); slots.push([d, "afternoon"]); }
  slots.push([5, "single"]); slots.push([6, "single"]);
  const slotLabel = ([d, k]) => `${DAY_SHORT[d]}${k === "morning" ? " AM" : k === "afternoon" ? " PM" : ""}`;
  const toggle = async (bc, slot) => {
    const has = bc.assignments.some((a) => a[0] === slot[0] && a[1] === slot[1]);
    const next = has ? bc.assignments.filter((a) => !(a[0] === slot[0] && a[1] === slot[1]))
      : [...bc.assignments, slot];
    try {
      await api.patch(`/manager/big-cleans/${bc.clean_id}`, { assignments: next });
      load();
      toast.success(`${bc.title}: ${has ? "removed from" : "added to"} ${slotLabel(slot)}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="manager-cleans">
      <p className="text-sm text-white/55">Each big clean appears once inside "Anytime during your shift" on its assigned shifts. Tap a slot to repeat a job on additional shifts or remove it.</p>
      {data.big_cleans.map((bc) => (
        <Panel key={bc.clean_id} className={`p-4 ${!bc.active ? "opacity-50" : ""}`} data-testid={`big-clean-${bc.slug}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold uppercase" style={{ fontFamily: "Anton, sans-serif" }}>{bc.title}</p>
              <p className="text-xs text-white/40">{bc.steps.length} steps · v{bc.version}</p>
            </div>
            <button data-testid={`big-clean-active-${bc.slug}`}
              onClick={async () => { await api.patch(`/manager/big-cleans/${bc.clean_id}`, { active: !bc.active }); load(); }}
              className="min-h-[40px] px-3 rounded-full border border-white/20 text-xs shrink-0">{bc.active ? "Disable" : "Enable"}</button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {slots.map((slot) => {
              const on = bc.assignments.some((a) => a[0] === slot[0] && a[1] === slot[1]);
              return (
                <button key={slot.join("-")} data-testid={`clean-slot-${bc.slug}-${slot.join("-")}`}
                  onClick={() => toggle(bc, slot)}
                  className={`min-h-[40px] px-2.5 rounded-full border text-xs ${on ? "border-[#ff2e4d] text-[#ff2e4d] bg-[#ff2e4d]/10" : "border-white/15 text-white/40"}`}>
                  {slotLabel(slot)}
                </button>
              );
            })}
          </div>
        </Panel>
      ))}
    </div>
  );
}

function VideosAdmin() {
  const [videos, setVideos] = useState(null);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [zone, setZone] = useState("");
  const load = () => api.get("/videos").then((r) => setVideos(r.data.videos)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!videos) return <Spinner />;
  const add = async () => {
    try {
      await api.post("/manager/videos", { title, url, zone, status: "published" });
      toast.success("Video published"); setTitle(""); setUrl(""); setZone(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = async (v, status) => {
    try { await api.patch(`/manager/videos/${v.video_id}`, { status }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="manager-videos">
      <Panel className="p-4 space-y-3 mb-5">
        <p className="text-xs uppercase tracking-widest text-white/40">Add training video (approved host URL)</p>
        <input data-testid="video-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className={inputCls} />
        <input data-testid="video-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… (YouTube/Vimeo/host link)" className={inputCls} />
        <input data-testid="video-zone-input" value={zone} onChange={(e) => setZone(e.target.value)} placeholder="Zone (optional)" className={inputCls} />
        <GhostBtn data-testid="video-add-button" onClick={add} disabled={!title.trim() || !url.trim()} className="w-full">Publish video</GhostBtn>
      </Panel>
      <div className="space-y-2">
        {videos.map((v) => (
          <div key={v.video_id} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#141414] px-3 py-2">
            <p className="text-sm truncate">{v.title} <span className="text-white/35 text-xs">· {v.status}</span></p>
            <div className="flex gap-1.5 shrink-0">
              {v.status !== "published" && <button onClick={() => setStatus(v, "published")} className="min-h-[40px] px-3 rounded-full border border-white/20 text-xs">Publish</button>}
              {v.status !== "archived" && <button onClick={() => setStatus(v, "archived")} className="min-h-[40px] px-3 rounded-full border border-white/20 text-xs">Archive</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Settings() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/manager/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!settings) return <Spinner />;
  const update = (i, field, val) => {
    const st = [...settings.admin_shifts];
    st[i] = { ...st[i], [field]: val };
    setSettings({ ...settings, admin_shifts: st });
  };
  const save = async () => {
    try {
      await api.patch("/manager/settings", { admin_shifts: settings.admin_shifts });
      toast.success("Shift settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const dayText = (s) => s.days.length === 5 ? "Mon–Fri" : "Sat & Sun";
  return (
    <div className="space-y-4" data-testid="manager-settings">
      <p className="text-sm text-white/55">Weekdays run Morning + Afternoon; weekends run one shift. Set real times when ready — nothing is guessed. Closure responsibility controls whether lock-up tasks appear and the default sign-off outcome.</p>
      {settings.admin_shifts.map((s, i) => (
        <Panel key={s.key} className="p-4">
          <p className="font-semibold uppercase mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
            {s.label} <span className="text-xs text-white/40 normal-case" style={{ fontFamily: "Archivo" }}>· {dayText(s)}{!s.configured ? " · times not configured" : ""}</span>
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start"><input data-testid={`shift-start-${s.key}`} type="time" value={s.start || ""} onChange={(e) => update(i, "start", e.target.value)} className={inputCls} /></Field>
            <Field label="End"><input data-testid={`shift-end-${s.key}`} type="time" value={s.end || ""} onChange={(e) => update(i, "end", e.target.value)} className={inputCls} /></Field>
          </div>
          <Field label="Closure responsibility">
            <select data-testid={`shift-closure-${s.key}`} value={s.closure} onChange={(e) => update(i, "closure", e.target.value)} className={inputCls + " mt-1.5"}>
              <option value="handover">Hands over — venue remains open</option>
              <option value="final">Responsible for final closure</option>
            </select>
          </Field>
        </Panel>
      ))}
      <BigBtn data-testid="settings-save-button" onClick={save}>Save shift settings</BigBtn>
      <p className="text-xs text-white/35">Shift reports & alerts email: {settings.report_email}</p>
    </div>
  );
}

function Outbox() {
  const [entries, setEntries] = useState(null);
  const load = () => api.get("/manager/outbox").then((r) => setEntries(r.data.outbox)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!entries) return <Spinner />;
  const COLOR = { sent: "text-emerald-400", pending: "text-amber-400", sending: "text-amber-400", failed: "text-[#ff2e4d]" };
  return (
    <div className="space-y-2" data-testid="manager-outbox">
      {entries.length === 0 && <p className="text-white/40 text-sm">No emails yet.</p>}
      {entries.map((e) => (
        <Panel key={e.email_id} className="p-3.5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm truncate">{e.subject}</p>
            <p className="text-xs text-white/40">{e.to} · <span className={COLOR[e.status]}>{e.status}</span> · {e.attempts} attempt(s){e.last_error ? ` · ${e.last_error}` : ""}</p>
          </div>
          {e.status !== "sent" && (
            <button data-testid={`outbox-resend-${e.email_id}`} onClick={async () => { await api.post(`/manager/outbox/${e.email_id}/resend`); toast.success("Re-queued"); setTimeout(load, 1500); }}
              aria-label="Resend" className="h-11 w-11 flex items-center justify-center rounded-lg border border-white/20 shrink-0"><RefreshCw size={16} /></button>
          )}
        </Panel>
      ))}
    </div>
  );
}

function Audit() {
  const [events, setEvents] = useState(null);
  useEffect(() => { api.get("/manager/audit").then((r) => setEvents(r.data.events)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!events) return <Spinner />;
  return (
    <div className="space-y-1.5 max-h-[65vh] overflow-y-auto" data-testid="manager-audit">
      {events.map((ev) => (
        <p key={ev.event_id} className="text-xs text-white/55 border-l-2 border-white/10 pl-3 py-0.5">
          <b className="text-white/80">{ev.actor}</b> ({ev.role}) — {ev.action} {ev.entity && <span className="text-white/35">{ev.entity}</span>}
          {ev.detail && <span className="text-white/35"> · {ev.detail}</span>}
          <span className="block text-white/25">{new Date(ev.at).toLocaleString()}</span>
        </p>
      ))}
    </div>
  );
}

export default function Manager() {
  const [authed, setAuthed] = useState(getProfile()?.kind === "manager");
  const [tab, setTab] = useState("Dashboard");
  if (!authed) return <ManagerLogin onDone={() => setAuthed(true)} />;
  const COMP = { Dashboard, Week, Requests, Reports, Tasks, Cleans, Videos: VideosAdmin, Settings, Outbox, Audit };
  const Active = COMP[tab];
  return (
    <Shell title="Manager console" back="/staff">
      <div className="flex gap-1.5 overflow-x-auto pb-3 -mx-4 px-4 mb-2" data-testid="manager-tabs">
        {TABS.map((t) => (
          <button key={t} data-testid={`manager-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`min-h-[40px] px-4 rounded-full border text-sm whitespace-nowrap ${tab === t ? "border-[#ff2e4d] text-[#ff2e4d] bg-[#ff2e4d]/10" : "border-white/15 text-white/55"}`}>{t}</button>
        ))}
      </div>
      <Active />
    </Shell>
  );
}
